import { ProviderNumber, SmsA2pRegistrationView } from "@tiny/api";
import { extractBackendError } from "@tiny/components/src/utils/errorUtils";
import { ChipColor } from "@tiny/components/src/Chip/Chip.types";

/**
 * What to show the merchant, from the backend's `nextAction`. Display only:
 * whether sending is allowed comes from `canLaunchCampaign`, never this.
 */
export type RegistrationPhase =
  | "not_started"
  | "pending"
  | "rejected"
  | "approved"
  | "suspended";

export function getRegistrationPhase(
  registration?: SmsA2pRegistrationView
): RegistrationPhase | undefined {
  if (!registration) return undefined;
  if (registration.status === "SUSPENDED") return "suspended";
  switch (registration.nextAction) {
    case "SUBMIT_DETAILS":
      return "not_started";
    case "WAIT":
      return "pending";
    case "FIX_AND_RESUBMIT":
      return "rejected";
    default:
      return registration.status === "APPROVED" ? "approved" : "pending";
  }
}

/** Only a carrier-approved number can send texts. */
export function isNumberVerified(
  number: Pick<ProviderNumber, "verificationStatus">
): boolean {
  return number.verificationStatus === "APPROVED";
}

/**
 * A number's toll-free verification as a chip, its meaning and next step.
 */
export function getNumberVerification(number: ProviderNumber): {
  label: string;
  color: ChipColor;
  hint: string;
  actionLabel?: string;
} {
  switch (number.verificationStatus) {
    case "IN_REVIEW":
      return {
        label: "In review",
        color: "yellow",
        hint: "Carriers usually reply within 3–5 business days.",
      };
    case "APPROVED":
      return { label: "Verified", color: "green", hint: "Can send texts." };
    case "REJECTED":
      return {
        label: "Needs changes",
        color: "red",
        hint:
          number.verificationError ||
          "Carriers asked for changes to this verification.",
        actionLabel: "Fix and resubmit",
      };
    default:
      return {
        label: "Not verified",
        color: "grey",
        hint: "Can't send texts until carriers verify it.",
        actionLabel: "Verify",
      };
  }
}

// Most urgent first: what the merchant has to act on comes before waiting.
const VERIFICATION_URGENCY = [
  "REJECTED",
  "NOT_SUBMITTED",
  "IN_REVIEW",
  "APPROVED",
] as const;

/**
 * The number most needing attention and how many share its status, so a
 * summary of several numbers never hides one that needs fixing.
 */
export function getMostUrgentNumber(
  numbers: ProviderNumber[]
): { number: ProviderNumber; count: number } | undefined {
  for (const status of VERIFICATION_URGENCY) {
    const matching = numbers.filter(
      (number) => (number.verificationStatus ?? "NOT_SUBMITTED") === status
    );
    if (matching.length > 0) {
      return { number: matching[0], count: matching.length };
    }
  }
  return undefined;
}

/** One line explaining why sending is blocked in each phase. */
export function getRegistrationBlockedMessage(
  registration?: SmsA2pRegistrationView
): string {
  switch (getRegistrationPhase(registration)) {
    case "not_started":
      return "Verify a toll-free number before you can send texts.";
    case "pending":
      return "Your toll-free verification is in review. You can send once carriers approve it — usually 3–5 business days.";
    case "rejected":
      return registration?.failureReason
        ? `Carriers asked for changes to your verification: ${registration.failureReason}`
        : "Carriers asked for changes to your verification. Update it and resubmit.";
    case "suspended":
      return "Your SMS registration is suspended. Contact support to restore sending.";
    default:
      return "";
  }
}

/** Stable SmsErrorCode from a backend error body — branch on this, not the message. */
export function getSmsErrorCode(error?: {
  errorCodes?: { errorCode?: string }[];
}): string | undefined {
  return error?.errorCodes?.[0]?.errorCode;
}

/**
 * The API client rejects with the axios error, so the backend body is on
 * `error.response`.
 */
export function extractSmsError(error: unknown) {
  const anyError = error as { response?: Record<string, unknown> } | undefined;
  return extractBackendError(
    (anyError?.response ?? anyError ?? {}) as Record<string, unknown>
  );
}
